import React, { useEffect, useState } from "react";
import { checkValidObject, dateTimeFormat, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import { useFormik } from "formik";
import apiGlobal from "global/api.global";
import env from "environment_system/env_system";
import {
  Row,
  Col,
  Card,
  CardHeader,
  Button,
  CardBody,
  CardFooter,
  Form,
  Input,
  Label,
} from "reactstrap";
import * as Yup from 'yup';
import { RootState } from "index";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import ErrorTooltip from "Components/ErrorTooltip";
import { ReportingTypeConstant, EventPrecedence } from "shared/constants";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import FormLoading from "Components/FormLoading";
import ErrorComponent from "Components/ErrorComponent";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface PilotDetailsComponentType {
  toggleAddTab: any;
  activeAddTab: number;
  toggleTab: any;
  activeTab: number;
  setErrorMessage: (value: boolean) => void;
  editReportBool: boolean;
}

const PilotDetailsComponent = ({
  toggleAddTab,
  activeAddTab,
  toggleTab,
  activeTab,
  setErrorMessage,
  editReportBool
}: PilotDetailsComponentType) => {
  /** State varibles */
  const { discardwarning } = useEditReport();
  const { VesselID, VoyageID, ReportID, CurrentReport, PreviousReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
  const pilotArrivalDetailsCurrentReport = CurrentReport?.data?.deck_officer?.lfonds_pilot_details ?? null;
  const pilotDepartureDetailsCurrentReport = CurrentReport?.data?.deck_officer?.lfonds_pilot_departure_details ?? null;
  const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
  const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
  const dispatch = useDispatch();
  const [formikKey, setFormikKey] = useState(0);
  const formikContext = useSafeFormikContext<any>();
  const setEditValues = formikContext?.setValues;
  const editInitialValues = formikContext?.initialValues;
  const editSubmit = formikContext?.submitForm;
  const editIsSubmitting = formikContext?.isSubmitting;
  /** State variables end */

  /** Assign values to initial object of Pilot Details' object */
  const getInitialPilotDetailsValues = () => {
    if (checkValidObject(editInitialValues?.deck_officer?.lfonds_pilot_details)) {
      const details = editInitialValues.deck_officer.lfonds_pilot_details;
      return {
        ...details,
        pilot_onboard: details.pilot_onboard ? dateTimeFormat(details.pilot_onboard) : null,
        pilot_disembarked: details.pilot_disembarked ? dateTimeFormat(details.pilot_disembarked) : null,
        etb: details.etb ? dateTimeFormat(details.etb) : null,
      };
    } else if (checkValidObject(pilotArrivalDetailsCurrentReport)) {
      return {
        ...pilotArrivalDetailsCurrentReport,
        pilot_onboard: pilotArrivalDetailsCurrentReport.pilot_onboard
          ? dateTimeFormat(pilotArrivalDetailsCurrentReport.pilot_onboard)
          : null,
        pilot_disembarked: pilotArrivalDetailsCurrentReport.pilot_disembarked
          ? dateTimeFormat(pilotArrivalDetailsCurrentReport.pilot_disembarked)
          : null,
        etb: pilotArrivalDetailsCurrentReport.etb
          ? dateTimeFormat(pilotArrivalDetailsCurrentReport.etb)
          : null,
      };
    } else {
      return {
        pilot_onboard: null,
        pilot_disembarked: null,
        etb: null,
        first_line_ashore: null,
        all_made_fast: null,
        nor: null,
        vessel: VesselID,
        voyage_information: VoyageID,
        vessel_reporting_information: ReportID,
      };
    }
  };

  /** Assign values to initial object of Pilot Details' object */
  const getInitialPilotDetailsDepartureValues = () => {
    let obj: any;
    if (checkValidObject(editInitialValues?.deck_officer?.lfonds_pilot_departure_details)) {
      return editInitialValues?.deck_officer?.lfonds_pilot_departure_details;
    } else if (checkValidObject(pilotDepartureDetailsCurrentReport)) {
      return pilotDepartureDetailsCurrentReport;
    } else {
      obj = {
        is_this_departure_report: false,
        water_density: 0,
        bending_moment_frame: 0,
        gm: 0,
        shearing_force: 0,
        shearing_force_frame: 0,
        bending_moment: 0,
        torsional_moment: 0,
        all_cast_off: null,
        vessel: VesselID,
        voyage_information: VoyageID,
        vessel_reporting_information: ReportID,
      };
      return obj;
    }
  };

  /** Pilot Details' formik object */
  const PilotDetailsFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      arrivalDetails: getInitialPilotDetailsValues(),
      departureDetails: getInitialPilotDetailsDepartureValues(),
    },
    validationSchema: Yup.object().shape({
      arrivalDetails: Yup.object().shape({
        pilot_onboard: Yup.date()
          .min(
            dateTimeFormat(previousVesselReport?.local_time as string),
            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
          ).max(
            dateTimeFormat(currentVesselReport?.local_time as string),
            commonValidationMessages.maxDateCurrentRecord
          ).nullable(),
        pilot_disembarked: Yup.date().min(
          dateTimeFormat(previousVesselReport?.local_time as string),
          `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
        ).max(
          dateTimeFormat(currentVesselReport?.local_time as string),
          commonValidationMessages.maxDateCurrentRecord
        ).nullable(),
        etb: Yup.date().min(
          dateTimeFormat(previousVesselReport?.local_time as string),
          `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
        ).max(
          dateTimeFormat(currentVesselReport?.local_time as string),
          commonValidationMessages.maxDateCurrentRecord
        ).nullable(),
      }),
      departureDetails: Yup.object().shape({
        water_density: Yup.number().typeError(`${commonValidationMessages.number}`),
        bending_moment_frame: Yup.number().typeError(`${commonValidationMessages.number}`),
        gm: Yup.number().typeError(`${commonValidationMessages.number}`),
        shearing_force: Yup.number().typeError(`${commonValidationMessages.number}`),
        shearing_force_frame: Yup.number().typeError(`${commonValidationMessages.number}`),
        bending_moment: Yup.number().typeError(`${commonValidationMessages.number}`),
        torsional_moment: Yup.number().typeError(`${commonValidationMessages.number}`),
      })
    }),
    onSubmit: async (values: any, actions: any) => {
      if (env?.form_validation === false) {
        setErrorMessage(null)
        toggleAddTab(activeAddTab + 1);
      }
      actions.setSubmitting(true);
      let responseArray: any = [];
      if (checkValidObject(pilotArrivalDetailsCurrentReport)) {
        responseArray.push(apiGlobal.put(`/lfonds_pilot_details/${values?.arrivalDetails?.id}/`, values?.arrivalDetails));
      } else {
        responseArray.push(apiGlobal.post(`/lfonds_pilot_details/`, values?.arrivalDetails));
      }
      if (checkValidObject(pilotDepartureDetailsCurrentReport)) {
        responseArray.push(apiGlobal.put(`/lfonds_pilot_departure_detail/${values?.departureDetails?.id}/`, values?.departureDetails));
      } else {
        responseArray.push(apiGlobal.post(`/lfonds_pilot_departure_detail/`, values?.departureDetails));
      }
      await handleServerResponse(responseArray).then(async (res: any) => {
        if (res === true) {
          dispatch(getCurrentReportAction(VesselID, ReportID));
          setFormikKey(formikKey + 1);
          if (env?.form_validation === true) {
            setErrorMessage(null)
            toggleAddTab(activeAddTab + 1);
          }
        } else {
          setErrorMessage(res)
        }
      })
      actions.setSubmitting(false);
    }
  })


  /** UseEffect */
  useEffect(() => {
    PilotDetailsFormik.initialValues.arrivalDetails = getInitialPilotDetailsValues();
    PilotDetailsFormik.initialValues.departureDetails = getInitialPilotDetailsDepartureValues();
    setFormikKey(formikKey + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pilotArrivalDetailsCurrentReport, pilotDepartureDetailsCurrentReport]);
  useEffect(() => {
    if (editInitialValues?.deck_officer) {
      setEditValues({
        ...editInitialValues,
        deck_officer: {
          ...editInitialValues?.deck_officer,
          lfonds_pilot_details: PilotDetailsFormik?.values?.arrivalDetails,
          lfonds_pilot_departure_details: PilotDetailsFormik?.values?.departureDetails
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PilotDetailsFormik?.values?.arrivalDetails, PilotDetailsFormik?.values?.departureDetails])
  /** UseEffect end */
  return (
    <Card className="p-0 mb-0">
      <CardHeader className="p-2">
        <div className="text-center">
          <Row>
            <Col>
              <h4 className="page_title pos-start mb-0">Pilot Details</h4>
              <p className="card-title-desc pos-start">
                All readings since last report
              </p>
            </Col>
          </Row>
        </div>
      </CardHeader>
      {reportingLoading && <FormLoading />}
      {reportingError && <ErrorComponent message="Error loading required data!" />}
      {!reportingLoading && !reportingError && (
        <Form autoComplete="off" onSubmit={PilotDetailsFormik?.handleSubmit} noValidate>
          <CardBody className="px-2 py-0 mt-2">
            <Row className="mb-2">
              <Col sm={3}>
                <Label className="mb-0" for='pilot_onboard'>Pilot onboard (Local Time)</Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  name="arrivalDetails.pilot_onboard"
                  type="datetime-local"
                  autoFocus={true}
                  max="9999-12-31T23:59"
                  className="form-control mt-0 text-uppercase datetimepicker"
                  id='pilot_onboard'
                  onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                  onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                  defaultValue={dateTimeFormat(
                    PilotDetailsFormik?.values?.arrivalDetails?.pilot_onboard,
                    false,
                    true
                  )}
                />
                {PilotDetailsFormik?.errors?.arrivalDetails && PilotDetailsFormik?.touched?.arrivalDetails &&
                  PilotDetailsFormik?.touched?.arrivalDetails?.pilot_onboard &&
                  PilotDetailsFormik?.errors?.arrivalDetails?.pilot_onboard && env?.form_validation === true &&
                  document.getElementById(`pilot_onboard`) !== null && !discardwarning &&
                  <ErrorTooltip
                    target={`pilot_onboard`}
                    message={PilotDetailsFormik?.errors?.arrivalDetails?.pilot_onboard}
                    open={!!PilotDetailsFormik?.errors.arrivalDetails}
                  />
                }
              </Col>
              <Col sm={3}>
                <Label className="mb-0" for="pilot_disembarked">
                  Pilot disembarked (Local Time)
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  name="arrivalDetails.pilot_disembarked"
                  type="datetime-local"
                  max="9999-12-31T23:59"
                  className="form-control mt-0 text-uppercase datetimepicker"
                  id="pilot_disembarked"
                  onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                  onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                  defaultValue={dateTimeFormat(
                    PilotDetailsFormik?.values?.arrivalDetails?.pilot_disembarked,
                    false,
                    true
                  )}
                />
                {PilotDetailsFormik?.errors?.arrivalDetails && PilotDetailsFormik?.touched?.arrivalDetails &&
                  PilotDetailsFormik?.touched?.arrivalDetails?.pilot_disembarked &&
                  PilotDetailsFormik?.errors?.arrivalDetails?.pilot_disembarked && env?.form_validation === true &&
                  document.getElementById(`pilot_disembarked`) !== null && !discardwarning &&
                  <ErrorTooltip
                    target={`pilot_disembarked`}
                    message={PilotDetailsFormik?.errors?.arrivalDetails?.pilot_disembarked}
                    open={!!PilotDetailsFormik?.errors.arrivalDetails}
                  />
                }
              </Col>
              <Col sm={3}>
                <Label className="mb-0" for="etb">
                  ETB (Local Time)
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  name="arrivalDetails.etb"
                  type="datetime-local"
                  max="9999-12-31T23:59"
                  className="form-control mt-0 text-uppercase datetimepicker"
                  id={"etb"}
                  onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                  onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                  disabled={
                    checkValidObject(currentVesselReport) &&
                      (currentVesselReport?.reporting_type === ReportingTypeConstant.INPORT ||
                        currentVesselReport?.event_precedence_id === EventPrecedence.END_OF_SEA_PASSAGE) &&
                      currentVesselReport?.event_precedence_id !== EventPrecedence.AT_BERTH
                      ? false : true
                  }
                  defaultValue={dateTimeFormat(
                    PilotDetailsFormik?.values?.arrivalDetails?.etb,
                    false,
                    true
                  )}
                />
                {PilotDetailsFormik?.errors?.arrivalDetails && PilotDetailsFormik?.touched?.arrivalDetails &&
                  PilotDetailsFormik?.touched?.arrivalDetails?.etb &&
                  PilotDetailsFormik?.errors?.arrivalDetails?.etb && env?.form_validation === true &&
                  document.getElementById(`etb`) !== null && !discardwarning &&
                  <ErrorTooltip
                    target={`etb`}
                    message={PilotDetailsFormik?.errors?.arrivalDetails?.etb}
                    open={!!PilotDetailsFormik?.errors.arrivalDetails}
                  />
                }
              </Col>
              <Col sm={3}>
                <Label className="mb-0" for="first_line_ashore">
                  First line ashore (Local Time)
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  name="arrivalDetails.first_line_ashore"
                  type="time"
                  className="form-control max-width-7"
                  id={"first_line_ashore"}
                  onChange={(e: any) => PilotDetailsFormik?.setFieldValue("arrivalDetails.first_line_ashore", e.target.value)}
                  defaultValue={PilotDetailsFormik?.values?.arrivalDetails?.first_line_ashore}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col sm={3}>
                <Label className="mb-0" for="all_made_fast">
                  All made fast (Local Time)
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  name="arrivalDetails.all_made_fast"
                  type="time"
                  className="form-control max-width-7"
                  id={"all_made_fast"}
                  onChange={(e: any) => PilotDetailsFormik?.setFieldValue("arrivalDetails.all_made_fast", e.target.value)}
                  defaultValue={PilotDetailsFormik?.values?.arrivalDetails?.all_made_fast}
                />
              </Col>
              <Col sm={3}>
                <Label className="mb-0" for="nor">
                  NOR (Local Time)
                </Label>
                <Input
                  onFocus={(e) => e.target.select()}
                  name={"arrivalDetails.nor"}
                  type="time"
                  className="form-control max-width-7"
                  id={"nor"}
                  onChange={(e: any) => PilotDetailsFormik?.setFieldValue("arrivalDetails.nor", e.target.value)}
                  defaultValue={PilotDetailsFormik?.values?.arrivalDetails?.nor}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={3}>
                <Label className="mb-0 w-5 mt-3" for="is_this_departure_report">
                  Is this a departure from port report?
                </Label>
              </Col>
              <Col>
                <div className="square-switch sqswitch mt-3">
                  <Input
                    onFocus={(e) => e.target.select()}
                    type="checkbox"
                    switch="none"
                    name={"departureDetails.is_this_departure_report"}
                    id="is_this_departure_report"
                    onChange={(e: any) => {
                      const checked = e.target.checked;
                      PilotDetailsFormik?.handleChange(e);

                      if (!checked) {
                        // Reset only specific fields, keep id/created_on/etc.
                        PilotDetailsFormik?.setValues({
                          ...PilotDetailsFormik.values,
                          departureDetails: {
                            ...PilotDetailsFormik.values.departureDetails, // preserve existing meta
                            is_this_departure_report: false,
                            water_density: 0,
                            bending_moment_frame: 0,
                            gm: 0,
                            shearing_force: 0,
                            shearing_force_frame: 0,
                            bending_moment: 0,
                            torsional_moment: 0,
                            all_cast_off: null,
                            vessel: VesselID,
                            voyage_information: VoyageID,
                            vessel_reporting_information: ReportID,
                          },
                        });
                      }
                    }}
                    checked={
                      PilotDetailsFormik?.values?.departureDetails?.is_this_departure_report
                    }
                  />
                  <Label
                    for="is_this_departure_report"
                    data-on-label="Yes"
                    data-off-label="No"
                  />
                </div>
              </Col>
            </Row>
            {PilotDetailsFormik?.values &&
              PilotDetailsFormik?.values?.departureDetails?.is_this_departure_report && (
                <React.Fragment>
                  <Row className="mb-2">
                    <Col sm={3}>
                      <Label className="mb-0" for="water_density">
                        Water density
                      </Label>
                      <div className="input-group">
                        <Input
                          onFocus={(e) => e.target.select()}
                          type="text"
                          id="water_density"
                          name="departureDetails.water_density"
                          className="form-control"
                          onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                          onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                          defaultValue={PilotDetailsFormik?.values?.departureDetails?.water_density}
                        />
                        <div className="input-group-text">gm/ml</div>
                      </div>
                      {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                        PilotDetailsFormik?.touched?.departureDetails?.water_density &&
                        PilotDetailsFormik?.errors?.departureDetails?.water_density &&
                        env?.form_validation === true && !discardwarning && (
                          <ErrorTooltip
                            target="water_density"
                            message={PilotDetailsFormik?.errors?.departureDetails.water_density}
                            open={
                              PilotDetailsFormik?.errors?.departureDetails.water_density
                                ? true
                                : false
                            }
                          />
                        )}
                    </Col>
                    <Col sm={3}>
                      <Label className="mb-0" for="gm">
                        GM
                      </Label>
                      <div className="input-group">
                        <Input
                          onFocus={(e) => e.target.select()}
                          type="text"
                          id="gm"
                          name="departureDetails.gm"
                          className="form-control"
                          onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                          onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                          defaultValue={PilotDetailsFormik?.values?.departureDetails?.gm}
                        />
                        <div className="input-group-text">m</div>
                      </div>
                      {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                        PilotDetailsFormik?.touched?.departureDetails?.gm &&
                        PilotDetailsFormik?.errors?.departureDetails?.gm &&
                        env?.form_validation === true && !discardwarning && (
                          <ErrorTooltip
                            target="gm"
                            message={PilotDetailsFormik?.errors?.departureDetails.gm}
                            open={
                              PilotDetailsFormik?.errors?.departureDetails.gm
                                ? true
                                : false
                            }
                          />
                        )}
                    </Col>
                    <Col sm={6}>
                      <Label className="mb-0" for="shearing_force">
                        Shearing force
                      </Label>
                      <div className="ele_row1">
                        <div className="input-group">
                          <Input
                            onFocus={(e) => e.target.select()}
                            type="text"
                            id="shearing_force"
                            name="departureDetails.shearing_force"
                            className="form-control"
                            onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                            onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                            defaultValue={PilotDetailsFormik?.values?.departureDetails?.shearing_force}
                          />
                          <div className="input-group-text">%</div>
                        </div>
                        {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                          PilotDetailsFormik?.touched?.departureDetails?.shearing_force &&
                          PilotDetailsFormik?.errors?.departureDetails?.shearing_force &&
                          env?.form_validation === true && !discardwarning && (
                            <ErrorTooltip
                              target="shearing_force"
                              message={PilotDetailsFormik?.errors?.departureDetails.shearing_force}
                              open={
                                PilotDetailsFormik?.errors?.departureDetails.shearing_force
                                  ? true
                                  : false
                              }
                            />
                          )}
                        <Label
                          className="mt-2 ml-1 mr-1"
                          for="shearing_force_frame"
                        >
                          @Frame{" "}
                        </Label>
                        <Input
                          onFocus={(e) => e.target.select()}
                          type="text"
                          id="shearing_force_frame"
                          name="departureDetails.shearing_force_frame"
                          className="form-control"
                          onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                          onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                          defaultValue={PilotDetailsFormik?.values?.departureDetails?.shearing_force_frame}
                        />
                      </div>
                      {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                        PilotDetailsFormik?.touched?.departureDetails?.shearing_force_frame &&
                        PilotDetailsFormik?.errors?.departureDetails?.shearing_force_frame &&
                        env?.form_validation === true && !discardwarning && (
                          <ErrorTooltip
                            target="shearing_force_frame"
                            message={PilotDetailsFormik?.errors?.departureDetails.shearing_force_frame}
                            open={
                              PilotDetailsFormik?.errors?.departureDetails.shearing_force_frame
                                ? true
                                : false
                            }
                          />
                        )}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <Label className="mb-0" for="bending_moment">
                        Bending moment
                      </Label>
                      <div className="ele_row1">
                        <div className="input-group">
                          <Input
                            onFocus={(e) => e.target.select()}
                            type="text"
                            id="bending_moment"
                            name="departureDetails.bending_moment"
                            className="form-control"
                            onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                            onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                            defaultValue={PilotDetailsFormik?.values?.departureDetails?.bending_moment}
                          />
                          <div className="input-group-text">%</div>
                        </div>
                        {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                          PilotDetailsFormik?.touched?.departureDetails?.bending_moment &&
                          PilotDetailsFormik?.errors?.departureDetails?.bending_moment &&
                          env?.form_validation === true && !discardwarning && (
                            <ErrorTooltip
                              target="bending_moment"
                              message={PilotDetailsFormik?.errors?.departureDetails.bending_moment}
                              open={
                                PilotDetailsFormik?.errors?.departureDetails.bending_moment
                                  ? true
                                  : false
                              }
                            />
                          )}
                        <Label
                          className="mt-2 ml-1 mr-1"
                          for="bending_moment_frame"
                        >
                          @Frame{" "}
                        </Label>
                        <Input
                          onFocus={(e) => e.target.select()}
                          type="text"
                          id="bending_moment_frame"
                          name="departureDetails.bending_moment_frame"
                          className="form-control"
                          onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                          onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                          defaultValue={PilotDetailsFormik?.values?.departureDetails?.bending_moment_frame}
                        />
                      </div>
                      {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                        PilotDetailsFormik?.touched?.departureDetails?.bending_moment_frame &&
                        PilotDetailsFormik?.errors?.departureDetails?.bending_moment_frame &&
                        env?.form_validation === true && !discardwarning && (
                          <ErrorTooltip
                            target="bending_moment_frame"
                            message={PilotDetailsFormik?.errors?.departureDetails.bending_moment_frame}
                            open={
                              PilotDetailsFormik?.errors?.departureDetails.bending_moment_frame
                                ? true
                                : false
                            }
                          />
                        )}
                    </Col>
                    <Col sm={3}>
                      <Label className="mb-0" for="torsional_moment">
                        Torsional moment
                      </Label>
                      <div className="input-group">
                        <Input
                          onFocus={(e) => e.target.select()}
                          type="text"
                          id="torsional_moment"
                          name="departureDetails.torsional_moment"
                          className="form-control"
                          onBlur={(e: any) => PilotDetailsFormik?.handleBlur(e)}
                          onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                          defaultValue={PilotDetailsFormik?.values?.departureDetails?.torsional_moment}
                        />
                        <div className="input-group-text">%</div>
                      </div>
                      {PilotDetailsFormik?.errors && PilotDetailsFormik?.touched &&
                        PilotDetailsFormik?.touched?.departureDetails?.torsional_moment &&
                        PilotDetailsFormik?.errors?.departureDetails?.torsional_moment &&
                        env?.form_validation === true && !discardwarning && (
                          <ErrorTooltip
                            target="torsional_moment"
                            message={PilotDetailsFormik?.errors?.departureDetails.torsional_moment}
                            open={
                              PilotDetailsFormik?.errors?.departureDetails.torsional_moment
                                ? true
                                : false
                            }
                          />
                        )}
                    </Col>
                    <Col sm={3}>
                      <Label className="mb-0" for="all_cast_off">
                        All cast off
                      </Label>
                      <Input
                        onFocus={(e) => e.target.select()}
                        name="departureDetails.all_cast_off"
                        type="time"
                        className="form-control max-width-7"
                        id={"all_cast_off"}
                        onChange={(e: any) => PilotDetailsFormik?.handleChange(e)}
                        defaultValue={PilotDetailsFormik?.values?.departureDetails?.all_cast_off}
                      />
                    </Col>
                  </Row>
                </React.Fragment>
              )}
          </CardBody>
          <CardFooter className="p-2 py-3">
            <Row className="d-flex align-items-center flex-row-reverse">
              <Col lg={10}>
                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                  {/* {editReportBool && (
                    <Button
                      type="reset"
                      color="danger"
                      className="btn_size_cstm"
                      onClick={() => {
                        setEditValues({
                          ...editValues,
                          deck_officer: {
                            ...editValues?.deck_officer,
                            lfonds_pilot_details: editInitialValues?.deck_officer?.lfonds_pilot_details,
                            lfonds_pilot_departure_details: editInitialValues?.deck_officer?.lfonds_pilot_departure_details,
                          }
                        });
                      }}
                    >
                      Reset
                    </Button>
                  )} */}
                  <Button
                    type={editReportBool ? "button" : "submit"}
                    color="primary"
                    className="btn_size_cstm"
                    disabled={PilotDetailsFormik?.isSubmitting || editIsSubmitting}
                    onClick={async () => {
                      const validationErrors = await PilotDetailsFormik?.validateForm();
                      Object.keys(PilotDetailsFormik?.values || {}).forEach((key: any) => {
                        PilotDetailsFormik?.setFieldTouched(key, true);
                      });
                      if (editReportBool && Object.keys(validationErrors).length === 0) {
                        await saveEditForm(editSubmit, toggleAddTab, activeAddTab);
                      }
                    }}
                  >
                    {(PilotDetailsFormik?.isSubmitting || editIsSubmitting) && (
                      <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                    )}
                    Next <i className="bx bx-chevron-right ms-1" />
                  </Button>
                </div>
              </Col>
              <Col lg={2}>
                <Button
                  type="button"
                  color="primary"
                  className="btn_size_cstm"
                  onClick={() => {
                    PilotDetailsFormik?.setErrors({});
                    setErrorMessage(null);
                    if (activeAddTab === 1) {
                      toggleTab(activeTab - 1);
                    } else {
                      toggleAddTab(activeAddTab - 1);
                    }
                  }}
                >
                  <i className="bx bx-chevron-left me-1" /> Previous
                </Button>
              </Col>
            </Row>
          </CardFooter>
          <FormValuesDebug
            values={[PilotDetailsFormik?.values, PilotDetailsFormik?.errors]}
          />
        </Form>
      )
      }
    </Card >
  );
};

export default PilotDetailsComponent;
